import React, { useEffect, useMemo } from "react";
import { Layer, Source } from "react-map-gl/maplibre";
import { Outlet } from "react-router";
import MapControl from "../MapControl";
import useLayerEvent from "../useLayerEvent";
import VigourLegend from "./VigourLegend";
import Popup from "../Popup";
import { FilterSpecification } from "maplibre-gl";
import { VariableRates } from "../../../routes/customer/_customer_id/taskmap/wizard/vigour/VigourTaskmap";
import {
  VigourFeatureCollection,
  VigourFeatureProperties,
} from "./layer.types";
import { CutoffRange } from "../../../routes/customer/_customer_id/taskmap/taskmap.types";

export type VigourLayerProps = {
  geojson: VigourFeatureCollection;
  cutoff?: CutoffRange;
  rates?: VariableRates;
  onHover?: (feature: VigourFeatureProperties | null) => void;
};

function defaultHover() {
  // do nothing
}

function getFilter({
  rates,
  cutoff,
}: {
  rates?: VariableRates;
  cutoff?: CutoffRange;
}) {
  if (rates?.size) {
    const visibleClasses = [...rates.entries()]
      .filter(([_, rate]) => rate > 0)
      .map(([vigourClass]) => vigourClass);
    return [
      "all",
      ["!=", ["get", "vigour_class"], 0],
      ["in", ["get", "vigour_class"], ["literal", visibleClasses]],
    ] as FilterSpecification;
  }

  return [
    "all",
    ["!=", ["get", "vigour_class"], 0],
    [">=", ["get", "vigour_class"], cutoff ? cutoff.min : 0],
    ["<=", ["get", "vigour_class"], cutoff ? cutoff.max : 7],
  ] as FilterSpecification;
}

export default function VigourLayer({
  geojson,
  cutoff,
  rates,
  onHover = defaultHover,
}: VigourLayerProps): React.ReactNode {
  useLayerEvent(
    "mouseenter",
    "vigour-fill",
    /* istanbul ignore next */ (event) => {
      if (event.features?.length) {
        onHover(event.features[0].properties as VigourFeatureProperties);
      }
    },
  );

  useEffect(() => {}, [rates]);
  const rootStyle = getComputedStyle(document.documentElement);
  const getMapFilter = useMemo(() => {
    return getFilter({ rates, cutoff });
  }, [rates, cutoff]);
  return (
    <>
      <Source id="vigour" type="geojson" data={geojson}>
        <Outlet />
        <Layer
          type="fill"
          id="vigour-fill"
          beforeId="result"
          filter={getMapFilter}
          paint={{
            "fill-color": [
              "interpolate",
              ["linear"],
              ["number", ["get", "vigour_class"]],
              0,
              rootStyle.getPropertyValue("--color-vigour-map-0"), // dark red
              1,
              rootStyle.getPropertyValue("--color-vigour-map-1"), // dark orange
              2,
              rootStyle.getPropertyValue("--color-vigour-map-2"), // orange
              3,
              rootStyle.getPropertyValue("--color-vigour-map-3"), // yellow
              4,
              rootStyle.getPropertyValue("--color-vigour-map-4"), // lime green
              5,
              rootStyle.getPropertyValue("--color-vigour-map-5"), // light green
              6,
              rootStyle.getPropertyValue("--color-vigour-map-6"), // grass green
              7,
              rootStyle.getPropertyValue("--color-vigour-map-7"), // dark green
            ],
            "fill-opacity": 0.7,
          }}
        />
        <Popup layer="vigour-fill" component={PopupInformation} />
      </Source>
      <MapControl position="bottom-left" className="legend">
        <VigourLegend />
      </MapControl>
    </>
  );
}

function PopupInformation({
  properties,
}: {
  properties: VigourFeatureProperties;
}) {
  if (!properties.vigour_class) {
    return null;
  }

  return <strong>{properties.vigour_class?.toFixed(0)}</strong>;
}
